import './init';
import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import './core/i18n/config';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Router } from './Router';
import 'dayjs/locale/de';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import dayjsRecur from 'dayjs-recur';

// styles
import 'sanitize.css';
import './core/styles/styles.scss';
import './core/styles/global.scss';
import './core/styles/typography.scss';
import './core/styles/modals.scss';
import './core/styles/calendar.scss';
import './core/styles/mixins/paddingMarginMixin.scss';

import { showToast } from '@bp/ui-components';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { IsDesktopContextProvider } from 'context/IsDesktop';
import i18n, { defaultNS } from './core/i18n/config';
import { showSuccessToast } from './utils/showSuccessToast';

CapacitorUpdater.notifyAppReady().catch();

// Capgo updater status toasts (non-blocking UX)
void CapacitorUpdater.addListener('updateAvailable', (state) => {
  try {
    const version = state?.bundle?.version ? ` (${state.bundle.version})` : '';
    showToast(i18n.t('toast.update.available', { version }), { type: 'info', autoClose: 3000 });
  } catch {
    /** */
  }
});

void CapacitorUpdater.addListener('downloadComplete', () => {
  try {
    showSuccessToast(i18n.t('toast.update.installedReloading'), 2500);
  } catch {
    /** */
  }
});

// dayjs plugins
dayjs.locale('de');
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(isToday);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(dayjsRecur);
dayjs.extend(utc);
dayjs.extend(customParseFormat);

// Sentry instrumentation
if (import.meta.env.VITE_APP_SENTRY_DSN !== 'false') {
  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENVIRONMENT,
    release: 'bp-frontend@' + import.meta.env.VITE_APP_VERSION,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
      Sentry.browserSessionIntegration(),
      Sentry.browserProfilingIntegration(),
    ],
    tracesSampleRate: import.meta.env.VITE_APP_SENTRY_TRACING_SAMPLE_RATE,
    tracePropagationTargets: [import.meta.env.VITE_APP_BACKEND_URI, 'localhost'],
    profilesSampleRate: import.meta.env.VITE_APP_SENTRY_TRACING_SAMPLE_RATE,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

function setVhCssVar() {
  document.documentElement.style.setProperty('--vh', window.innerHeight * 0.01 + 'px');
}

setVhCssVar();
window.addEventListener('resize', setVhCssVar);
window.addEventListener('orientationchange', setVhCssVar);

const container = document.getElementById('root');
const root = createRoot(container as Element);
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary>
      <I18nextProvider i18n={i18n} defaultNS={defaultNS}>
        <IsDesktopContextProvider>
          <Router />
        </IsDesktopContextProvider>
      </I18nextProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
